/**
 * The two lanes every cell and timeline draws (SW-8879): production readiness
 * on top (green / red / grey), lake activity below (busy / available /
 * offline), both hatched where the day has not happened yet. The colours
 * differ in lightness as well as hue, so the lanes read without colour
 * vision; the readiness lane carries its reason as text wherever it fits.
 */
import React from "react";
import { styled } from "styled-components";
import { Segment } from "./cells";
import { tokens } from "../TractorState/tokens";

export const LANE_COLORS = {
  f: "#8fd79f",
  n: "#c6262b",
  u: "#d6d9de",
  b: "#0a3fa8",
  a: "#8fb8ff",
  o: "#eef0f3",
} as const;

export const HATCH =
  "repeating-linear-gradient(135deg, #f1f2f4 0 4px, #fff 4px 7px)";

export const Lane = styled.div<{ $thin?: boolean; $tall?: boolean }>`
  display: flex;
  height: ${(p) => (p.$tall ? 22 : p.$thin ? 7 : 10)}px;
  border-radius: ${(p) => (p.$tall ? 5 : 3)}px;
  overflow: hidden;
  background: ${LANE_COLORS.o};
  > span {
    display: block;
    height: 100%;
    flex: none;
  }
  > .f {
    background: ${LANE_COLORS.f};
  }
  > .n {
    background: ${LANE_COLORS.n};
  }
  > .u {
    background: ${LANE_COLORS.u};
  }
  > .b {
    background: ${LANE_COLORS.b};
  }
  > .a {
    background: ${LANE_COLORS.a};
  }
  > .o {
    background: ${LANE_COLORS.o};
  }
  > .t {
    background: ${HATCH};
  }
`;

export const Segments: React.FC<{ segments: Segment[] }> = ({ segments }) => (
  <>
    {segments.map((s, i) => (
      <span key={i} className={s.cls} style={{ width: `${s.w}%` }} />
    ))}
  </>
);

export const Swatch = styled.span<{ $cls: keyof typeof LANE_COLORS | "t" }>`
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  margin-right: 5px;
  vertical-align: -1px;
  background: ${(p) => (p.$cls === "t" ? HATCH : LANE_COLORS[p.$cls])};
  border: ${(p) =>
    p.$cls === "t" || p.$cls === "o" ? `1px solid ${tokens.line}` : "0"};
`;

export const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
  align-items: center;
`;

export const LaneLegend: React.FC = () => (
  <LegendRow aria-label="Legend">
    <strong style={{ color: tokens.ink, fontWeight: 600 }}>Top</strong>
    <span>
      <Swatch $cls="f" /> Production ready
    </span>
    <span>
      <Swatch $cls="n" /> Not ready (PMI, maintenance, fault…)
    </span>
    <span>
      <Swatch $cls="u" /> No mark
    </span>
    <strong style={{ color: tokens.ink, fontWeight: 600, marginLeft: 8 }}>
      Bottom
    </strong>
    <span>
      <Swatch $cls="b" /> Busy
    </span>
    <span>
      <Swatch $cls="a" /> Available
    </span>
    <span>
      <Swatch $cls="o" /> Offline
    </span>
    <span>
      <Swatch $cls="t" /> Not yet
    </span>
  </LegendRow>
);
